import toPairs from 'lodash/toPairs';
import { types as sdkTypes } from './sdkLoader';
import { diffInTime } from './dates';
import parsePhoneNumber from "libphonenumber-js";


const { LatLng, Money } = sdkTypes;

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

function validateStringDifference(primaryStr, secondaryStr, count) {
  // Split the strings into arrays of words
  const primaryWords = primaryStr.split(' ');
  const secondaryWords = secondaryStr.split(' ');

  // Initialize a counter for differences
  let differences = 0;

  // Loop through the words and count differences
  for (let i = 0; i < Math.max(primaryWords.length, secondaryWords.length); i++) {
    if (primaryWords[i] !== secondaryWords[i]) {
      differences++;
    }
  };

  return differences >= count;
};

const isNonEmptyString = val => {
  return typeof val === 'string' && val.trim().length > 0;
};

/**
 * Strip HTML tags to count plain text characters.
 */
export const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || '';
};

/**
 * Validator functions and helpers for Final Forms
 */

// Final Form expects and undefined value for a successful validation
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return message;
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : message;
  }
  return VALID;
};

export const requiredStringNoTrim = message => value => {
  return typeof value === 'string' && value.length > 0 ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredBoolean = message => value => {
  return typeof value === 'boolean' ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredAndNonEmptyString = message => value => {
  return isNonEmptyString(value) ? VALID : message;
};

export const requiredFieldArrayCheckbox = message => value => {
  if (!value) {
    return message;
  }

  const entries = toPairs(value);
  const hasSelectedValues = entries.filter(e => !!e[1]).length > 0;
  return hasSelectedValues ? VALID : message;
};

export const minLength = (message, minimumLength) => value => {
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length >= minimumLength ? VALID : message;
};

export const maxLength = (message, maximumLength) => value => {
  if (!value) {
    return VALID;
  }
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length <= maximumLength ? VALID : message;
};

export const minimumQuantity = (message, minimumQuantity) => value => {
  return Number(value) >= minimumQuantity ? VALID : message;
}

export const maxQuantity = (message, maxQuantity) => value => {
  if (!value) return VALID;
  return Number(value) <= maxQuantity ? VALID : message;
}

export const nonEmptyArray = message => value => {
  return value && Array.isArray(value) && value.length > 0 ? VALID : message;
};

export const autocompleteSearchRequired = message => value => {
  return value && value.search ? VALID : message;
};

export const autocompletePlaceSelected = message => value => {
  const selectedPlaceIsValid =
    value &&
    value.selectedPlace &&
    value.selectedPlace.address;
  return selectedPlaceIsValid ? VALID : message;
};

export const bookingDateRequired = inValidDateMessage => value => {
  const dateIsValid = value && value.date instanceof Date;
  return !dateIsValid ? inValidDateMessage : VALID;
};

export const bookingDatesRequired = (inValidStartDateMessage, inValidEndDateMessage) => value => {
  const startDateIsValid = value && value.startDate instanceof Date;
  const endDateIsValid = value && value.endDate instanceof Date;

  if (!startDateIsValid) {
    return inValidStartDateMessage;
  } else if (!endDateIsValid) {
    return inValidEndDateMessage;
  } else {
    return VALID;
  }
};

// Source: http://www.regular-expressions.info/email.html
// See the link above for an explanation of the tradeoffs.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const emailFormatValid = message => value => {
  return value && EMAIL_RE.test(value) ? VALID : message;
};

export const moneySubUnitAmountAtLeast = (message, minValue) => value => {
  if (!value) return VALID;
  return value instanceof Money && value.amount >= minValue ? VALID : message;
};

export const minimumPriceAtLeast = (message, minValue) => value => {
  if (!value || !Number(value)) return VALID;
  return Number(value) && value >= minValue ? VALID : message
}

const parseNum = str => {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
};

export const numberAtLeast = (message, minNumber) => value => {
  const valueNum = parseNum(value);
  return typeof valueNum === 'number' && valueNum >= minNumber ? VALID : message;
};

export const ageAtLeast = (message, minYears) => value => {
  const { year, month, day } = value;
  const dayNum = parseNum(day);
  const monthNum = parseNum(month);
  const yearNum = parseNum(year);

  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = new Date();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = diffInTime(now, age, 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

export const validBusinessURL = message => value => {
  if (typeof value !== 'string' || value.trim() === '') {
    return message;
  }

  let url;
  try {
    url = new URL(value);
  } catch {
    return message; // Invalid URL structure
  }

  // Ensure protocol is http or https
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return message;
  }

  // Disallow localhost, example.com, and IP addresses
  const hostname = url.hostname.toLowerCase();

  // Disallow localhost or example.com
  if (hostname === 'localhost' || hostname === 'example.com') {
    return message;
  }

  // Disallow IP addresses (IPv4 or IPv6)
  const isIP = /^(?:\d{1,3}\.){3}\d{1,3}$|^\[[0-9a-f:]+\]$/i.test(hostname);
  if (isIP) {
    return message;
  }

  return VALID;
};


export const validSsnLast4 = message => value => {
  return value.length === 4 ? VALID : message;
};

export const validHKID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  // HKID format example: AB364912(5)
  // ID can start with one or two letters and the check digit in the end can be in brackets or not
  if (value.length < 8) {
    return message;
  }

  // Handle possible brackets in value
  if (value.charAt(value.length - 3) === '(' && value.charAt(value.length - 1) === ')') {
    value = value.substring(0, value.length - 3) + value.charAt(value.length - 2);
  }
  value = value.toUpperCase();

  // Check that pattern is correct and split value to array
  const hkidPattern = /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
  const matchArray = value.match(hkidPattern);

  if (!matchArray) {
    return message;
  }

  const charPart = matchArray[1];
  const numPart = matchArray[2];
  const checkDigit = matchArray[3];

  // Calculate the checksum for character part.
  // Transfer letters to numbers so that A=10, B=11, C=12 etc.
  // If there is only one letter in the ID use 36 as the first value
  // Total calculation is weighted so that 1st digit is x9, 2nd digit x8, 3rd digit x7 etc.

  const strValidChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let checkSum = 0;

  if (charPart.length === 2) {
    checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
    checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
  } else {
    checkSum += 9 * 36;
    checkSum += 8 * (10 + strValidChars.indexOf(charPart));
  }

  // Calculate the checksum for numeric part

  for (let i = 0, j = 7; i < numPart.length; i++, j--) {
    checkSum += j * numPart.charAt(i);
  }

  // Verify the check digit
  const remaining = checkSum % 11;
  let verify = remaining === 0 ? 0 : 11 - remaining;
  verify = verify.toString();
  const isValid = verify === checkDigit || (verify === 10 && checkDigit === 'A');

  return isValid ? VALID : message;
};

export const validSGID = message => value => {
  return value.length === 9 ? VALID : message;
};

export const isSameCurrency = (message, comparableCurrency) => value => {
  if (!value) return VALID;
  return value.currency === comparableCurrency
    ? VALID
    : message;
}

export const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), VALID);


export const minimumAmount = (message, requiredAmount) => value => {
  const valueAsNumber = Number(value?.amount);
  if (!valueAsNumber) return VALID;
  return valueAsNumber < requiredAmount ? message : VALID;
}

export const stringValidator = ({ message, currentStr, shouldDifferentBy }) => value => {
  const isValid = validateStringDifference(currentStr, value, shouldDifferentBy);
  return isValid
    ? VALID
    : message;
}

export const positiveValue = (message) => value => {
  return !!Number(value) && (Number(value) > 0) ? VALID : message
}

export const phoneNumberValidator = (message, country) => value => {
  const phoneNumber = parsePhoneNumber(value, country)
  return phoneNumber && phoneNumber.isValid() ? VALID : message;
}

export const minimumRichTextLength = (message, minimumLength) => value => {
  const strippedValue = stripHtml(value || '');  // Ensure empty value works correctly.

  const hasLength = typeof strippedValue === 'string' && strippedValue.length >= minimumLength;

  return hasLength ? VALID : message;
};

export const maximumRichTextLength = (message, maximumLength) => value => {
  const strippedValue = stripHtml(value || '');  // Ensure empty value works correctly.

  const hasLength = typeof strippedValue === 'string' && strippedValue.length <= maximumLength;

  return hasLength ? VALID : message;
};